import React from 'react';
import {
  X,
  MessageSquare,
  Scissors,
  Calendar,
  FileText,
  Tag,
  Edit2,
  Trash2,
  Copy,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DevTooltip from './DevTooltip';
import type { PDFSnippet } from '../services/api';
import logger from '../utils/simpleLogger';

interface SavedMessage {
  id: string;
  content: string;
  title?: string;
  notes?: string;
  tags?: string[];
  sources_count: number;
  created_at: string;
  updated_at: string;
}

interface ViewSavedItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SavedMessage | PDFSnippet | null;
  itemType: 'message' | 'snippet';
  onDelete?: () => void;
  onEdit?: () => void;
  onOpenInViewer?: () => void; // Para snippets: abrir en PDF viewer
}

const ViewSavedItemModal: React.FC<ViewSavedItemModalProps> = ({
  isOpen,
  onClose,
  item,
  itemType,
  onDelete,
  onEdit,
  onOpenInViewer,
}) => {
  const { theme } = useTheme();

  if (!isOpen || !item) return null;

  const isMessage = itemType === 'message';
  const message = isMessage ? (item as SavedMessage) : null;
  const snippet = !isMessage ? (item as PDFSnippet) : null;

  // Debug: Log snippet data
  if (snippet) {
    logger.debug('📸 ViewSavedItemModal - Snippet data:', {
      id: snippet.id,
      title: snippet.title,
      hasImageData: !!snippet.image_data,
      imageDataLength: snippet.image_data?.length,
      imageDataPreview: snippet.image_data?.substring(0, 50),
      hasTextContent: !!snippet.text_content,
      snippet_type: snippet.snippet_type,
    });
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Fecha no disponible';
    try {
      const date = new Date(dateString);
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const handleCopyContent = () => {
    const content = isMessage ? message?.content : snippet?.text_content;
    if (content) {
      navigator.clipboard.writeText(content);
      alert('Contenido copiado al portapapeles');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className={`${theme.colors.bg.primary} rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <DevTooltip componentName="ViewSavedItemModal.tsx">
          {/* Header */}
          <div
            className={`px-6 py-4 border-b ${theme.colors.border.primary} flex items-center justify-between sticky top-0 ${theme.colors.bg.primary} z-10`}
          >
            <div className="flex items-center gap-3">
              {isMessage ? (
                <MessageSquare className={`w-5 h-5 ${theme.colors.text.primary}`} />
              ) : (
                <Scissors className={`w-5 h-5 ${theme.colors.text.primary}`} />
              )}
              <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>
                {isMessage ? 'Mensaje Guardado' : 'Fragmento PDF'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className={`p-1 rounded ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} transition-colors`}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Título */}
            {(message?.title || snippet?.title) && (
              <div>
                <h4 className={`text-xl font-semibold ${theme.colors.text.primary} mb-2`}>
                  {message?.title || snippet?.title}
                </h4>
              </div>
            )}

            {/* Vista previa de imagen (solo para snippets) - PRIMERO */}
            {snippet?.image_data && (
              <div>
                <label className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block`}>
                  Vista previa:
                </label>
                <div
                  className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-2 flex justify-center`}
                >
                  <img
                    src={snippet.image_data}
                    alt="Fragmento"
                    className="max-w-full max-h-96 rounded object-contain"
                  />
                </div>
              </div>
            )}

            {/* Contenido de texto */}
            {(isMessage || snippet?.text_content) && (
              <div
                className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-4`}
              >
                <label className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block`}>
                  {isMessage ? 'Mensaje:' : 'Texto extraído:'}
                </label>
                <div
                  className={`${theme.colors.text.primary} whitespace-pre-wrap text-sm leading-relaxed`}
                >
                  {isMessage ? message?.content : snippet?.text_content}
                </div>
              </div>
            )}

            {/* Notas */}
            {(message?.notes || snippet?.notes) && (
              <div>
                <label
                  className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block flex items-center gap-1`}
                >
                  <FileText size={14} />
                  Notas:
                </label>
                <div
                  className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}
                >
                  <p className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap`}>
                    {message?.notes || snippet?.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Tags */}
            {((message?.tags && message.tags.length > 0) ||
              (snippet?.tags && snippet.tags.length > 0)) && (
              <div>
                <label
                  className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block flex items-center gap-1`}
                >
                  <Tag size={14} />
                  Etiquetas:
                </label>
                <div className="flex flex-wrap gap-2">
                  {(message?.tags || snippet?.tags || []).map((tag, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center px-2 py-1 ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} rounded text-xs`}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata del snippet */}
            {snippet && (
              <div
                className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3 space-y-2`}
              >
                <p className={`text-xs font-medium ${theme.colors.text.secondary}`}>
                  Información del documento:
                </p>
                <div className="space-y-1">
                  <p className={`text-xs ${theme.colors.text.primary}`}>
                    📄 <strong>Documento:</strong> {snippet.document_name || 'Documento'}
                  </p>
                  <p className={`text-xs ${theme.colors.text.primary}`}>
                    📑 <strong>Página:</strong> {snippet.page_number}
                  </p>
                  {snippet.document_filename && (
                    <p className={`text-xs ${theme.colors.text.primary}`}>
                      📎 <strong>Archivo:</strong> {snippet.document_filename}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Metadata del mensaje */}
            {message && message.sources_count > 0 && (
              <div
                className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}
              >
                <p className={`text-xs ${theme.colors.text.primary}`}>
                  📚 <strong>Fuentes consultadas:</strong> {message.sources_count} documentos
                </p>
              </div>
            )}

            {/* Fechas */}
            {item.created_at && (
              <div className={`text-xs ${theme.colors.text.secondary} flex items-center gap-4`}>
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  Guardado: {formatDate(item.created_at)}
                </span>
                {item.updated_at && item.updated_at !== item.created_at && (
                  <span>Actualizado: {formatDate(item.updated_at)}</span>
                )}
              </div>
            )}
          </div>

          {/* Footer con acciones */}
          <div
            className={`px-6 py-4 border-t ${theme.colors.border.primary} flex justify-between gap-3 sticky bottom-0 ${theme.colors.bg.primary}`}
          >
            <div className="flex gap-2">
              {/* Copiar contenido */}
              <button
                onClick={handleCopyContent}
                className={`px-3 py-2 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-sm font-medium flex items-center gap-2`}
              >
                <Copy size={16} />
                Copiar
              </button>

              {/* Abrir en visor */}
              {onOpenInViewer && (
                <button
                  onClick={() => {
                    onOpenInViewer();
                    onClose();
                  }}
                  className={`px-3 py-2 rounded ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} hover:${theme.colors.bg.tertiary} transition-colors text-sm font-medium flex items-center gap-2`}
                >
                  <FileText size={16} />
                  {isMessage ? 'Ver en visor' : 'Ver en PDF'}
                </button>
              )}
            </div>

            <div className="flex gap-2">
              {/* Editar */}
              {onEdit && (
                <button
                  onClick={() => {
                    onEdit();
                    onClose();
                  }}
                  className={`px-3 py-2 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-sm font-medium flex items-center gap-2`}
                >
                  <Edit2 size={16} />
                  Editar
                </button>
              )}

              {/* Eliminar */}
              {onDelete && (
                <button
                  onClick={() => {
                    if (
                      confirm(
                        `¿Estás seguro de que deseas eliminar este ${
                          isMessage ? 'mensaje' : 'fragmento'
                        }?`
                      )
                    ) {
                      onDelete();
                      onClose();
                    }
                  }}
                  className={`px-3 py-2 rounded ${theme.colors.action.danger} ${theme.colors.text.inverse} hover:${theme.colors.action.danger} transition-colors text-sm font-medium flex items-center gap-2`}
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </DevTooltip>
      </div>
    </div>
  );
};

export default ViewSavedItemModal;
